import React, { useState } from "react";
import { Box, Collapse, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import EastRoundedIcon from "@mui/icons-material/EastRounded";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";

/**
 * What one edit changed (History dialog → Edits): "3 fields changed ▾" opens a list of ONLY the edited fields,
 * label · old → new. The server records them (backend shared/editDiff.js); an edit made before that shows nothing here.
 */
const EditChanges = ({ changes = [], more = 0, index = 0, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!Array.isArray(changes) || !changes.length) return null;
  const total = changes.length + (more || 0);
  return (
    <Box sx={{ mt: 0.75 }}>
      <Box
        component="button"
        type="button"
        data-testid={`edit-changes-toggle-${index}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          border: "1px solid #C9D7E8",
          backgroundColor: open ? "#EEF3F9" : "#fff",
          color: NAVY,
          borderRadius: "999px",
          px: 1,
          py: "2px",
          font: "inherit",
          fontFamily: FONT,
          fontSize: "11.5px",
          fontWeight: 600,
          cursor: "pointer",
          "&:hover": { backgroundColor: "#EEF3F9" },
        }}
      >
        {total} {total === 1 ? "field" : "fields"} changed
        <ExpandMoreRoundedIcon sx={{ fontSize: 16, transition: "transform 0.15s", transform: open ? "rotate(180deg)" : "none" }} />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          data-testid={`edit-changes-${index}`}
          sx={{ mt: 1, border: "1px solid #EAECF0", borderRadius: "10px", overflow: "hidden", backgroundColor: "#fff" }}
        >
          {changes.map((c, i) => (
            <Box
              key={c.field || i}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "minmax(120px, 30%) 1fr" },
                gap: { xs: "2px", sm: 1.5 },
                alignItems: "center",
                px: 1.5,
                py: "7px",
                backgroundColor: i % 2 ? "#FAFBFC" : "#fff",
                borderTop: i ? "1px solid #F2F4F7" : "none",
              }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#475467", fontFamily: FONT, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.label || c.field}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, flexWrap: "wrap" }}>
                <Typography
                  component="span"
                  sx={{ fontSize: "12px", fontFamily: FONT, color: c.from ? "#B42318" : "#98A2B3", textDecoration: c.from ? "line-through" : "none", wordBreak: "break-word" }}
                >
                  {c.from || "empty"}
                </Typography>
                <EastRoundedIcon sx={{ fontSize: 14, color: "#98A2B3" }} />
                <Typography component="span" sx={{ fontSize: "12px", fontFamily: FONT, fontWeight: 600, color: c.to ? "#067647" : "#98A2B3", wordBreak: "break-word" }}>
                  {c.to || "empty"}
                </Typography>
              </Box>
            </Box>
          ))}
          {more ? (
            <Typography sx={{ px: 1.5, py: "6px", fontSize: "11.5px", color: "#667085", fontFamily: FONT, borderTop: "1px solid #F2F4F7" }}>
              … and {more} more
            </Typography>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
};

export default EditChanges;
